import { useCallback } from 'react';
import { OutboundMessageStatus } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { ManualSmsScheduledV1 } from '@frontend/api-manual-scheduled-sms';
import { InboxLocationPhones } from '@frontend/inbox-location-phones';
import { useAppScopeStore } from '@frontend/scope';
import { GetWebsocketEventHandler, useWebsocketEventSubscription } from '@frontend/websocket';

/**
 * A hook that returns a function to execute when a manual scheduled SMS event is received.
 * This doesn't have any WebSocket subscription logic, just the handler. Useful for testing.
 */
export const useSMSManualScheduledWSHandler = () => {
  const { deleteScheduledSms, upsertScheduledSms, updateScheduledSms } =
    ManualSmsScheduledV1._QueryUpdaters.useQueryUpdaters();

  const { selectedLocationIds } = useAppScopeStore();
  const checkUserLocationPhoneAccess = InboxLocationPhones.Hooks.useCheckUserLocationPhoneAccess(selectedLocationIds);

  const wsHandler = useCallback<GetWebsocketEventHandler<NotificationType.SMS_MANUAL_SCHEDULED>>(
    (event) => {
      const {
        params: { scheduledSms },
      } = event;

      // If User does not have access to location phone number in the event's thread, ignore the event
      if (!!scheduledSms.locationPhone && !checkUserLocationPhoneAccess(scheduledSms.locationPhone)) {
        return;
      }

      if (!scheduledSms) return;

      if (scheduledSms.status === OutboundMessageStatus.DELETED) {
        deleteScheduledSms({
          id: scheduledSms.id,
          locationId: scheduledSms.locationId,
          threadId: scheduledSms.threadId,
        });
      } else if (scheduledSms.status === OutboundMessageStatus.SCHEDULED) {
        upsertScheduledSms(scheduledSms);
      } else {
        updateScheduledSms({
          matchValues: { id: scheduledSms.id, locationId: scheduledSms.locationId, threadId: scheduledSms.threadId },
          newValues: scheduledSms,
        });
      }
    },
    [checkUserLocationPhoneAccess, deleteScheduledSms, upsertScheduledSms, updateScheduledSms]
  );

  return wsHandler;
};

export const useSMSManualScheduledWSSubscription = () => {
  const handler = useSMSManualScheduledWSHandler();
  return useWebsocketEventSubscription(NotificationType.SMS_MANUAL_SCHEDULED, handler);
};
